import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import ImageLightbox from "@/components/ImageLightbox";
import SearchBar from "@/components/SearchBar";
import BookmarkBtn from "@/components/BookmarkBtn";
import PollsPanel from "@/components/PollsPanel";
import PollGate from "@/components/PollGate";
import MentionText from "@/components/MentionText";
import MentionAutocomplete from "@/components/MentionAutocomplete";
import { GenderBadge } from "@/components/GenderBadge";
import UpgradeGuestModal from "@/components/UpgradeGuestModal";
import { api, wsUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, Flag, Users, Smile, ArrowLeft, MessageCircle, Share2, Trash2, Image as ImageIcon, Mic, Square, Lock, Reply, X, CornerUpLeft, Bell, BellOff, Pencil } from "lucide-react";

const EMOJIS = ["🔥", "💀", "😂", "❤️", "🤔", "👀", "🫡", "💯"];

export default function Room() {
  const { slug } = useParams();
  const { user, token } = useAuth();
  const nav = useNavigate();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [input, setInput] = useState("");
  const [reactingTo, setReactingTo] = useState(null);
  const [showReport, setShowReport] = useState(null);
  const [reportReason, setReportReason] = useState("");
  const [replyingTo, setReplyingTo] = useState(null); // {id, username, body_preview} or null
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [wsStatus, setWsStatus] = useState("connecting"); // "connected" | "connecting" | "closed"
  const [threadOpen, setThreadOpen] = useState(null); // origin msg id or null
  const [jumpBack, setJumpBack] = useState(null); // {id, username} or null
  const [lightbox, setLightbox] = useState(null); // {src, name} or null
  const [roomFollow, setRoomFollow] = useState({ following: false, follower_count: 0 });
  const [searchQ, setSearchQ] = useState("");
  const [bookmarks, setBookmarks] = useState(new Set());
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordStartRef = useRef(0);
  const pingIntervalRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const composerInputRef = useRef(null);
  const mentionKeyHandlerRef = useRef(null);

  const scrollBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: rm }, { data: msgs }] = await Promise.all([
        api.get(`/rooms/${slug}`),
        api.get(`/rooms/${slug}/messages?limit=100`),
      ]);
      setRoom(rm);
      const list = Array.isArray(msgs) ? msgs : (msgs?.messages || []);
      setMessages(list);
      setHasMore(!!(msgs && msgs.has_more));
      setMembers(rm.members || []);
      scrollBottom();
    } catch (e) {
      toast.error(formatErr(e));
      nav("/lobby");
    }
  }, [slug, nav]);

  const loadOlder = useCallback(async () => {
    if (loadingOlder || !hasMore || messages.length === 0) return;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const prevHeight = el ? el.scrollHeight : 0;
    const prevTop = el ? el.scrollTop : 0;
    try {
      const oldest = messages[0]?.created_at;
      const { data } = await api.get(
        `/rooms/${slug}/messages?limit=100&before=${encodeURIComponent(oldest)}`
      );
      const older = data?.messages || [];
      if (older.length) {
        setMessages((cur) => [...older, ...cur]);
        // Preserve scroll position so the user stays anchored to what they were reading.
        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTop = prevTop + (scrollRef.current.scrollHeight - prevHeight);
          }
        });
      }
      setHasMore(!!data?.has_more);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoadingOlder(false);
    }
  }, [slug, messages, hasMore, loadingOlder]);

  useEffect(() => { load(); }, [load]);

  // Load user's bookmarks so the star icons render pre-selected.
  useEffect(() => {
    if (!user || user.is_guest) return;
    let alive = true;
    api.get("/bookmarks").then(({ data }) => {
      if (!alive) return;
      const ids = new Set((data.bookmarks || []).map((b) => b.message_id));
      setBookmarks(ids);
    }).catch(() => {});
    return () => { alive = false; };
  }, [user?.id, user?.is_guest]);

  // Handle ?highlight=<msg_id> from bookmarks / share links — scroll + flash.
  useEffect(() => {
    const hid = searchParams.get("highlight");
    if (!hid || messages.length === 0) return;
    // Give the DOM a beat to render, then locate + flash.
    const t = setTimeout(() => {
      const el = document.getElementById(`msg-${hid}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("msg-flash");
        setTimeout(() => el.classList.remove("msg-flash"), 1800);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [searchParams, messages.length]);

  // Load follow state
  useEffect(() => {
    let alive = true;
    api.get(`/rooms/${slug}/follow`).then(({ data }) => {
      if (alive) setRoomFollow(data);
    }).catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  const toggleRoomFollow = async () => {
    const wasFollowing = roomFollow.following;
    try {
      const { data } = wasFollowing
        ? await api.delete(`/rooms/${slug}/follow`)
        : await api.post(`/rooms/${slug}/follow`);
      setRoomFollow(data);
      toast(wasFollowing
        ? `Unfollowed #${slug}`
        : `Following #${slug} — you'll be pinged on new activity`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  // WebSocket connection with keepalive ping + auto-reconnect. Kubernetes
  // ingresses often close idle WS after ~60s, so send a ping every 25s and
  // reconnect with exponential backoff on unexpected drops.
  useEffect(() => {
    if (!token) return;
    shouldReconnectRef.current = true;

    const connect = () => {
      const url = `${wsUrl(`/ws/rooms/${slug}`)}?token=${encodeURIComponent(token)}`;
      setWsStatus("connecting");
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsStatus("connected");
        reconnectAttemptsRef.current = 0;
        // Start keepalive pings
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === 1) {
            try { ws.send(JSON.stringify({ type: "ping" })); } catch (e) { void e; }
          }
        }, 25000);
      };
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === "message") {
            setMessages((prev) => prev.some((x) => x.id === data.message.id) ? prev : [...prev, data.message]);
            scrollBottom();
          } else if (data.type === "reaction") {
            setMessages((prev) => prev.map((m) => m.id === data.message.id ? data.message : m));
          } else if (data.type === "message_edited") {
            setMessages((prev) => prev.map((m) => m.id === data.message.id ? { ...m, ...data.message } : m));
          } else if (data.type === "message_deleted") {
            setMessages((prev) => prev.filter((m) => m.id !== data.id));
          } else if (data.type === "presence") {
            setMembers(data.members || []);
          } else if (data.type === "typing") {
            if (data.user_id === user?.id) return;
            setTypingUsers((t) => ({ ...t, [data.user_id]: { username: data.username, at: Date.now() } }));
            setTimeout(() => {
              setTypingUsers((t) => {
                const nx = { ...t };
                if (nx[data.user_id] && Date.now() - nx[data.user_id].at >= 2500) delete nx[data.user_id];
                return nx;
              });
            }, 3000);
          } else if (data.type === "system") {
            /* system message */ void 0;
          } else if (data.type === "pong") {
            /* keepalive ack */ void 0;
          }
        } catch (err) { void err; }
      };
      ws.onclose = (ev) => {
        if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }
        // Hard closes we should NOT auto-reconnect on:
        // 4401 = bad token, 4403 = not allowed, 4404 = room gone, 4410 = room deleted, 1000 = normal close
        if (ev && (ev.code === 4401 || ev.code === 4404 || ev.code === 4410)) {
          shouldReconnectRef.current = false;
          setWsStatus("closed");
          return;
        }
        if (ev && ev.code === 4403) {
          shouldReconnectRef.current = false;
          setWsStatus("closed");
          toast.error("THIS ROOM ISN'T LIVE YET.");
          nav("/lobby");
          return;
        }
        if (!shouldReconnectRef.current) {
          setWsStatus("closed");
          return;
        }
        // Auto-reconnect with capped exponential backoff
        setWsStatus("connecting");
        reconnectAttemptsRef.current += 1;
        const delay = Math.min(15000, 500 * Math.pow(2, reconnectAttemptsRef.current - 1));
        reconnectTimerRef.current = setTimeout(connect, delay);
      };
      ws.onerror = () => { void 0; };
    };

    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      try { wsRef.current && wsRef.current.close(1000, "unmount"); } catch (e) { void e; }
    };
  }, [slug, token, user?.id, nav]);

  const send = (e) => {
    e?.preventDefault();
    const body = input.trim();
    if (!body) return;
    if (!wsRef.current || wsRef.current.readyState !== 1) {
      toast.error("Reconnecting… try again in a second.");
      return;
    }
    const payload = { type: "message", body };
    if (replyingTo) payload.reply_to = replyingTo;
    wsRef.current.send(JSON.stringify(payload));
    setInput("");
    setReplyingTo(null);
  };

  const onChange = (e) => {
    setInput(e.target.value);
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    if (typingTimerRef.current) return;
    wsRef.current.send(JSON.stringify({ type: "typing" }));
    typingTimerRef.current = setTimeout(() => { typingTimerRef.current = null; }, 1500);
  };

  const onComposerKeyDown = (e) => {
    // Let the mention autocomplete intercept ArrowUp/ArrowDown/Enter/Tab/Escape
    // when it's open. If it doesn't handle the key, the form's Enter-submit
    // behavior still fires normally.
    const h = mentionKeyHandlerRef.current;
    if (h) h(e);
  };

  const react = async (messageId, emoji) => {
    try {
      await api.post(`/rooms/${slug}/react/${messageId}`, { emoji });
      setReactingTo(null);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const report = async () => {
    if (!reportReason.trim()) return;
    try {
      await api.post(`/reports`, { message_id: showReport, room_slug: slug, reason: reportReason });
      toast.success("REPORT SUBMITTED · MODS NOTIFIED");
      setShowReport(null);
      setReportReason("");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const deleteRoom = async () => {
    try {
      await api.delete(`/rooms/${slug}`);
      toast.success(`ROOM ${room?.name || slug} DELETED`);
      nav("/lobby");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const sendAttachment = (kind, attachment_url, attachment_type, duration_ms) => {
    if (!wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ type: "message", kind, body: "", attachment_url, attachment_type, duration_ms }));
  };

  const pickImage = () => fileInputRef.current?.click();

  const onImagePicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8 MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      sendAttachment("image", data.url, data.content_type);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Mic not supported on this browser."); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recordStartRef.current = Date.now();
      mr.ondataavailable = (ev) => ev.data.size > 0 && audioChunksRef.current.push(ev.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        const duration = Date.now() - recordStartRef.current;
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", new File([blob], "voice.webm", { type: blob.type }));
          const { data } = await api.post("/uploads/audio", fd, { headers: { "Content-Type": "multipart/form-data" } });
          sendAttachment("audio", data.url, data.content_type, duration);
        } catch (err) {
          toast.error(formatErr(err));
        } finally {
          setUploading(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (err) {
      toast.error("Mic permission denied.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  // Build originId -> [reply messages] map so we can render "X REPLIES" chips
  // and populate the thread modal without extra network calls.
  const repliesByOrigin = useMemo(() => {
    const map = {};
    for (const m of messages) {
      const oid = m.reply_to?.id;
      if (!oid) continue;
      (map[oid] ||= []).push(m);
    }
    return map;
  }, [messages]);

  const jumpToMessage = (id) => {
    const el = document.getElementById(`msg-${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("msg-flash");
    setTimeout(() => el.classList.remove("msg-flash"), 1600);
  };

  const threadOrigin = threadOpen ? messages.find((x) => x.id === threadOpen) : null;
  const threadReplies = threadOpen ? (repliesByOrigin[threadOpen] || []) : [];

  if (!room) return <div className="min-h-screen flex items-center justify-center font-mono text-acid">CONNECTING…</div>;

  const activeTypers = Object.values(typingUsers);

  return (
    <div className="min-h-screen flex flex-col">
      <TopBar />

      <div className="flex-1 flex flex-col md:flex-row" data-testid="room-page">
        {wsStatus !== "connected" && (
          <div
            className="fixed top-14 left-0 right-0 z-30 bg-blaze text-obsidian font-mono text-xs tracking-widest text-center py-1 animate-pulse"
            data-testid="ws-reconnect-banner"
          >
            {wsStatus === "connecting" ? "// RECONNECTING TO ROOM…" : "// DISCONNECTED — REFRESH TO REJOIN"}
          </div>
        )}
        {/* Chat main */}
        <section className="flex-1 flex flex-col min-h-0">
          {/* Room header */}
          <div className="border-b border-white/10 px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/lobby" className="text-white/40 hover:text-acid" data-testid="btn-back">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <div className="font-mono text-[10px] tracking-widest" style={{ color: room.color }}>
                  {room.preset ? "PRESET" : `BY @${room.created_by}`}
                </div>
                <h1 className="font-heading text-3xl md:text-4xl leading-none" data-testid="room-title">{room.name}</h1>
                <p className="font-mono text-white/50 text-xs mt-1">&#47;&#47; {room.topic}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 font-mono text-xs">
              {!user?.is_guest && (
                <button
                  onClick={toggleRoomFollow}
                  data-testid={roomFollow.following ? "room-unfollow-btn" : "room-follow-btn"}
                  className={`flex items-center gap-1 px-2 py-1 border ${
                    roomFollow.following
                      ? "text-hotpink border-hotpink hover:bg-hotpink hover:text-obsidian"
                      : "text-white/50 hover:text-acid border-white/20 hover:border-acid"
                  }`}
                  title={roomFollow.following ? "Unfollow — no more pings for this room" : "Follow — get pinged when new activity happens here"}
                >
                  {roomFollow.following ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
                  {roomFollow.following ? "FOLLOWING" : "FOLLOW"}
                </button>
              )}
              <button
                onClick={async () => {
                  const { shareLink, roomShareUrl } = await import("@/lib/share");
                  const url = roomShareUrl(slug);
                  const res = await shareLink({
                    title: `#${room?.name || slug} · NITEWIRE`,
                    text: `Come chat in #${room?.name || slug} on NITEWIRE — ${room?.topic || "always-live rooms"}`,
                    url,
                  });
                  if (res.method === "copy" && res.ok) toast.success("SHARE LINK COPIED");
                  else if (res.method === "copy") toast.error("Copy failed.");
                  else if (res.method === "native" && res.ok) toast.success("SHARED");
                }}
                data-testid="room-share-btn"
                className="text-white/50 hover:text-acid flex items-center gap-1 border border-white/20 hover:border-acid px-2 py-1"
                title="Share this room"
              >
                <Share2 className="w-3 h-3" /> SHARE
              </button>
              {room && !room.preset && user?.username && room.created_by === user.username && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  data-testid="room-delete-btn"
                  className="text-white/50 hover:text-blaze flex items-center gap-1 border border-white/20 hover:border-blaze px-2 py-1"
                  title="Delete this room"
                >
                  <Trash2 className="w-3 h-3" /> DELETE
                </button>
              )}
              <div className="w-2 h-2 bg-acid animate-pulse" />
              <span>{members.length} LIVE</span>
            </div>
          </div>

          {/* Polls above the search bar / message list */}
          <PollsPanel slug={slug} user={user} />
          {/* Gate: pinned poll blocks entry until voted (creator + admin bypass) */}
          <PollGate slug={slug} user={user} />

          {/* Search bar — registered users only */}
          {user && !user.is_guest && (
            <SearchBar onQuery={setSearchQ} placeholder="Search this room…" testid="room-search" />
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-8 py-6 space-y-2" data-testid="messages-list">
            {hasMore && (
              <div className="flex justify-center pb-2">
                <button
                  type="button"
                  onClick={loadOlder}
                  disabled={loadingOlder}
                  data-testid="load-older-btn"
                  className="font-mono text-[10px] px-4 py-2 border-2 border-white/20 text-white/70 hover:border-acid hover:text-acid uppercase tracking-widest disabled:opacity-50"
                >
                  {loadingOlder ? "LOADING…" : "Load older messages"}
                </button>
              </div>
            )}
            {messages.length === 0 && (
              <div className="text-white/40 font-mono text-sm">&#47;&#47; no messages yet · say something first</div>
            )}
            {(searchQ.trim()
              ? messages.filter((m) => (m.body || "").toLowerCase().includes(searchQ.trim().toLowerCase()))
              : messages
            ).map((m) => (
              <MessageRow
                key={m.id}
                m={m}
                me={user}
                roomSlug={slug}
                bookmarked={bookmarks.has(m.id)}
                onBookmarkChange={(is) => setBookmarks((s) => {
                  const n = new Set(s);
                  if (is) n.add(m.id); else n.delete(m.id);
                  return n;
                })}
                replyCount={repliesByOrigin[m.id]?.length || 0}
                onReact={(emoji) => react(m.id, emoji)}
                onOpenReact={() => setReactingTo(m.id)}
                reactingOpen={reactingTo === m.id}
                onReport={() => setShowReport(m.id)}
                onReply={() => setReplyingTo({
                  id: m.id,
                  username: m.username,
                  body_preview: (m.body || (m.kind === "image" ? "[image]" : m.kind === "audio" ? "[voice note]" : "")).slice(0, 120),
                })}
                onOpenThread={() => setThreadOpen(m.id)}
                onOpenImage={(src, name) => setLightbox({ src, name })}
                onEdit={async (newBody) => {
                  try {
                    await api.patch(`/messages/${m.id}`, { body: newBody });
                    // Optimistic: WS will also push the update
                    setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, body: newBody, edited: true } : x));
                  } catch (e) {
                    toast.error(formatErr(e));
                  }
                }}
                onDelete={async () => {
                  if (!window.confirm("Delete this message? This can't be undone.")) return;
                  try {
                    await api.delete(`/messages/${m.id}`);
                    setMessages((prev) => prev.filter((x) => x.id !== m.id));
                  } catch (e) {
                    toast.error(formatErr(e));
                  }
                }}
                onJumpToOriginal={(fromMsg) => {
                  setJumpBack({ id: fromMsg.id, username: fromMsg.username });
                  const el = document.getElementById(`msg-${fromMsg.reply_to.id}`);
                  if (!el) return;
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                  el.classList.add("msg-flash");
                  setTimeout(() => el.classList.remove("msg-flash"), 1600);
                }}
              />
            ))}
            {activeTypers.length > 0 && (
              <div className="text-white/50 font-mono text-xs mt-2">
                {activeTypers.map(t => t.username).join(", ")} typing<span className="typing-dots"><span>.</span><span>.</span><span>.</span></span>
              </div>
            )}
          </div>

          {/* Composer */}
          {replyingTo && (
            <div
              className="border-t border-white/10 px-4 md:px-6 py-2 flex items-center gap-3 bg-panel"
              data-testid="reply-pill"
            >
              <Reply className="w-3 h-3 text-acid" />
              <div className="flex-1 min-w-0 font-mono text-[11px]">
                <div className="text-white/50">
                  REPLYING TO <span className="text-acid">@{replyingTo.username}</span>
                </div>
                <div className="text-white/70 truncate">{replyingTo.body_preview || "(attachment)"}</div>
              </div>
              <button
                type="button"
                onClick={() => setReplyingTo(null)}
                data-testid="cancel-reply"
                className="text-white/40 hover:text-blaze p-1"
                title="Cancel reply"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <form onSubmit={send} className="border-t border-white/10 p-4 md:p-6 flex gap-3 items-center flex-wrap relative" data-testid="composer">
            <MentionAutocomplete
              inputRef={composerInputRef}
              value={input}
              members={members}
              myId={user?.id}
              onSelect={(next, caret) => {
                setInput(next);
                // Restore caret to just after the inserted handle on next tick.
                requestAnimationFrame(() => {
                  const el = composerInputRef.current;
                  if (el) {
                    el.focus();
                    try { el.setSelectionRange(caret, caret); } catch { /* noop */ }
                  }
                });
              }}
              onKeyBind={(handler) => { mentionKeyHandlerRef.current = handler; }}
            />
            <span className="font-mono text-acid text-xs">&gt;</span>
            <input
              ref={composerInputRef}
              data-testid="chat-input"
              value={input}
              onChange={onChange}
              onKeyDown={onComposerKeyDown}
              placeholder={recording ? "recording voice note…" : "type something worth reading… @ to mention"}
              className="input-brutal border-white/10 flex-1 min-w-[200px]"
              maxLength={1000}
              disabled={recording}
              autoComplete="off"
            />
            {user?.is_guest ? (
              <button
                type="button"
                onClick={() => setUpgradeOpen(true)}
                className="font-mono text-[10px] tracking-widest text-acid flex items-center gap-1 border-2 border-acid/40 hover:border-acid hover:bg-acid hover:text-obsidian px-3 py-2 transition-colors uppercase"
                data-testid="guest-lockout-nudge"
                title={`Add email + password to keep @${user.username}`}
              >
                <Lock className="w-3 h-3" /> REGISTER FOR VOICE + IMAGES
              </button>
            ) : (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={onImagePicked}
                  className="hidden"
                  data-testid="image-input"
                />
                <button
                  type="button"
                  onClick={pickImage}
                  disabled={uploading || recording}
                  data-testid="btn-image"
                  className="border-2 border-white/20 hover:border-acid p-2 disabled:opacity-40"
                  title="Send an image or GIF"
                >
                  <ImageIcon className="w-4 h-4" />
                </button>
                {recording ? (
                  <button
                    type="button"
                    onClick={stopRecording}
                    data-testid="btn-stop-record"
                    className="border-2 border-blaze bg-blaze/20 text-blaze p-2 animate-pulse"
                    title="Stop and send"
                  >
                    <Square className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startRecording}
                    disabled={uploading}
                    data-testid="btn-record"
                    className="border-2 border-white/20 hover:border-acid p-2 disabled:opacity-40"
                    title="Record a voice note"
                  >
                    <Mic className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
            <button data-testid="send-button" type="submit" className="btn-acid flex items-center gap-2" disabled={!input.trim() || recording}>
              <Send className="w-4 h-4" /> SEND
            </button>
          </form>
        </section>

        {/* Sidebar — members */}
        <aside className="w-full md:w-72 border-t md:border-t-0 md:border-l border-white/10 bg-surface p-4 md:p-6" data-testid="members-sidebar">
          <div className="flex items-center gap-2 font-mono text-xs text-white/60 tracking-widest">
            <Users className="w-3 h-3" /> WHO&apos;S HERE — {members.length}
          </div>
          <div className="mt-4 space-y-2 max-h-[40vh] md:max-h-none overflow-y-auto">
            {members.length === 0 && <div className="font-mono text-white/30 text-xs">&#47;&#47; nobody yet · you&apos;re first in</div>}
            {members.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-3 group border border-transparent hover:border-white/20 p-2"
                data-testid={`member-${m.username}`}
              >
                <Link to={m.id === user?.id ? "/profile" : `/u/${m.username}`} className="shrink-0" title={`View @${m.username}'s profile`}>
                  <img src={m.avatar} alt="" className="w-8 h-8 object-cover border border-white/10 hover:border-acid" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link
                    to={m.id === user?.id ? "/profile" : `/u/${m.username}`}
                    className="font-mono text-sm truncate hover:text-acid inline-flex items-center gap-1"
                    data-testid={`member-name-${m.username}`}
                  >
                    <span className="truncate">@{m.username}</span>
                    <GenderBadge gender={m.gender} className="text-sm shrink-0" />
                  </Link>
                  {m.id !== user?.id && (
                    <Link
                      to={`/dm/${m.id}`}
                      className="font-mono text-[10px] text-white/40 hover:text-acid flex items-center gap-1"
                      data-testid={`member-dm-${m.username}`}
                    >
                      <MessageCircle className="w-3 h-3" /> DM
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {/* Reaction popup */}
      {reactingTo && (
        <div className="fixed inset-0 z-30" onClick={() => setReactingTo(null)}>
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-obsidian border-2 border-acid flex" onClick={(e) => e.stopPropagation()} data-testid="emoji-picker">
            {EMOJIS.map((e) => (
              <button key={e} onClick={() => react(reactingTo, e)} className="p-3 text-2xl hover:bg-acid hover:text-obsidian transition-colors">
                {e}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Image lightbox */}
      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          downloadName={lightbox.name}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Report modal */}
      {showReport && (
        <div className="fixed inset-0 bg-obsidian/90 z-40 flex items-center justify-center p-4" onClick={() => setShowReport(null)}>
          <div className="bg-obsidian border-2 border-blaze w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono text-blaze text-xs tracking-widest">// REPORT MESSAGE</div>
            <h3 className="font-heading text-3xl mt-1">TELL US WHY.</h3>
            <textarea data-testid="report-reason" className="input-brutal mt-4 min-h-[100px]" value={reportReason}
                      onChange={(e) => setReportReason(e.target.value)} placeholder="What's wrong with it?" />
            <div className="flex gap-3 mt-4">
              <button className="btn-brutal flex-1" onClick={() => setShowReport(null)}>CANCEL</button>
              <button data-testid="submit-report" onClick={report} className="btn-acid flex-1" style={{ background: "#FF3B30", borderColor: "#FF3B30", color: "#050505" }}>REPORT</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete room modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-obsidian/90 z-40 flex items-center justify-center p-4" onClick={() => setConfirmDelete(false)} data-testid="room-delete-modal">
          <div className="bg-obsidian border-2 border-blaze w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono text-blaze text-xs tracking-widest">// DELETE ROOM</div>
            <h3 className="font-heading text-4xl mt-2">BURN IT DOWN?</h3>
            <p className="font-mono text-white/70 text-xs mt-3">
              &gt; This deletes <span className="text-acid">{room?.name}</span>, all its messages, and boots anyone still inside. No undo.
            </p>
            <div className="flex gap-3 mt-6">
              <button className="btn-brutal flex-1" onClick={() => setConfirmDelete(false)}>CANCEL</button>
              <button
                data-testid="room-delete-confirm"
                onClick={deleteRoom}
                className="btn-acid flex-1"
                style={{ background: "#FF3B30", borderColor: "#FF3B30", color: "#050505" }}
              >
                DELETE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Jump-back pill — after clicking a reply's quoted preview */}
      {jumpBack && (
        <button
          type="button"
          onClick={() => {
            jumpToMessage(jumpBack.id);
            setJumpBack(null);
          }}
          data-testid="jump-back-pill"
          className="fixed bottom-24 right-6 z-30 bg-acid text-obsidian border-2 border-obsidian shadow-[4px_4px_0_#00E5FF] font-mono text-xs tracking-widest uppercase px-3 py-2 flex items-center gap-2 hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform"
          title={`Back to @${jumpBack.username}'s reply`}
        >
          <CornerUpLeft className="w-4 h-4" /> BACK TO @{jumpBack.username}
        </button>
      )}

      {/* Thread modal — click a "X REPLIES" chip to see them all */}
      {threadOpen && threadOrigin && (
        <div
          className="fixed inset-0 bg-obsidian/90 z-40 flex items-center justify-center p-4"
          onClick={() => setThreadOpen(null)}
          data-testid="thread-modal"
        >
          <div className="bg-obsidian border-2 border-acid w-full max-w-lg max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono text-acid text-xs tracking-widest">// THREAD · {threadReplies.length} {threadReplies.length === 1 ? "REPLY" : "REPLIES"}</div>
              <button onClick={() => setThreadOpen(null)} className="text-white/40 hover:text-blaze" data-testid="thread-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => { jumpToMessage(threadOrigin.id); setThreadOpen(null); }}
              className="w-full text-left border-l-2 border-acid pl-3 py-2 mb-4 hover:bg-acid/10"
              data-testid="thread-origin"
            >
              <div className="font-mono text-xs text-acid">@{threadOrigin.username}</div>
              <div className="font-body text-sm text-white/90 mt-1">
                {threadOrigin.body || (threadOrigin.kind === "image" ? "[image]" : threadOrigin.kind === "audio" ? "[voice note]" : "")}
              </div>
            </button>
            <div className="space-y-2">
              {threadReplies.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => { jumpToMessage(r.id); setThreadOpen(null); }}
                  data-testid={`thread-reply-${r.id}`}
                  className="w-full text-left border border-white/10 hover:border-acid p-3"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <img src={r.avatar} alt="" className="w-5 h-5 object-cover border border-white/10" />
                    <span className="font-mono text-xs text-white">@{r.username}</span>
                    <span className="font-mono text-[10px] text-white/40">
                      {new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <div className="font-body text-sm break-words" style={{ color: r.text_color || "#F4F4F0" }}>
                    {r.body || (r.kind === "image" ? "[image]" : r.kind === "audio" ? "[voice note]" : "")}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Guest → registered upgrade */}
      <UpgradeGuestModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}

// EndRoom
function MessageRow({ m, me, roomSlug, bookmarked, onBookmarkChange, onReact, onOpenReact, reactingOpen, onReport, onReply, onOpenThread, onJumpToOriginal, onOpenImage, onEdit, onDelete, replyCount }) {
  const time = new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const mine = me?.id === m.user_id;
  const canEdit = mine && m.kind === "text";
  const canDelete = mine || !!me?.is_admin;
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(m.body || "");
  React.useEffect(() => { setDraft(m.body || ""); }, [m.body]);
  const saveEdit = () => {
    const next = draft.trim();
    if (!next || next === m.body) { setEditing(false); return; }
    onEdit && onEdit(next);
    setEditing(false);
  };
  const reactions = Object.entries(m.reactions || {});
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  return (
    <div className="group flex items-start gap-3 py-1 hover:bg-white/[0.02] px-2 -mx-2 transition-colors" data-testid={`msg-${m.id}`} id={`msg-${m.id}`}>
      <Link to={mine ? "/profile" : `/u/${m.username}`} className="shrink-0 mt-1" title={`View @${m.username}'s profile`}>
        <img src={m.avatar} alt="" className="w-6 h-6 object-cover border border-white/10 hover:border-acid" />
      </Link>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-mono text-[10px] text-white/40">[{time}]</span>
          <Link
            to={mine ? "/profile" : `/u/${m.username}`}
            className={`font-mono text-sm hover:underline ${mine ? "text-acid" : "text-white hover:text-acid"}`}
            data-testid={`msg-user-${m.id}`}
          >
            @{m.username}
          </Link>
          <GenderBadge gender={m.gender} className="text-sm" />
        </div>
        {m.reply_to && (
          <button
            type="button"
            onClick={() => onJumpToOriginal && onJumpToOriginal(m)}
            className="block border-l-2 border-acid pl-2 mt-1 mb-1 text-xs opacity-70 hover:opacity-100 max-w-md text-left hover:bg-acid/10 transition-colors w-fit"
            data-testid={`reply-ref-${m.id}`}
            title="Jump to original message"
          >
            <div className="font-mono text-[10px] text-acid">↳ @{m.reply_to.username}</div>
            <div className="font-body text-white/70 text-xs truncate">{m.reply_to.body_preview || "(attachment)"}</div>
          </button>
        )}
        <div className="font-body text-[15px] leading-relaxed break-words" style={{ color: m.text_color || "#F4F4F0" }}>
          {m.kind === "image" && m.attachment_url ? (
            <button
              type="button"
              onClick={() => onOpenImage && onOpenImage(`${backend}${m.attachment_url}`, `nitewire-${m.id}.png`)}
              className="block mt-1 p-0 border-0 bg-transparent cursor-zoom-in group/img"
              data-testid={`msg-image-btn-${m.id}`}
              title="Click to enlarge / download"
            >
              <img
                src={`${backend}${m.attachment_url}`}
                alt=""
                className="max-w-xs max-h-64 border border-white/10 group-hover/img:border-acid transition-colors"
                loading="lazy"
                data-testid={`msg-image-${m.id}`}
              />
            </button>
          ) : m.kind === "audio" && m.attachment_url ? (
            <div className="flex items-center gap-2 border border-white/10 p-2 mt-1 w-fit" data-testid={`msg-audio-${m.id}`}>
              <audio controls preload="metadata" src={`${backend}${m.attachment_url}`} className="max-w-xs" />
              {m.duration_ms ? (
                <span className="font-mono text-[10px] text-white/50">{Math.round(m.duration_ms / 1000)}s</span>
              ) : null}
            </div>
          ) : editing ? (
            <div className="flex flex-wrap items-center gap-2 mt-1" data-testid={`msg-edit-${m.id}`}>
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                  if (e.key === "Escape") { setDraft(m.body || ""); setEditing(false); }
                }}
                maxLength={1000}
                className="flex-1 min-w-[180px] bg-transparent border border-acid px-2 py-1 font-body text-sm focus:outline-none"
                data-testid={`msg-edit-input-${m.id}`}
              />
              <button type="button" onClick={saveEdit} className="font-mono text-[10px] px-2 py-1 border border-acid text-acid hover:bg-acid hover:text-obsidian uppercase tracking-widest" data-testid={`msg-edit-save-${m.id}`}>SAVE</button>
              <button type="button" onClick={() => { setDraft(m.body || ""); setEditing(false); }} className="font-mono text-[10px] px-2 py-1 border border-white/20 text-white/60 hover:border-white/40 uppercase tracking-widest" data-testid={`msg-edit-cancel-${m.id}`}>CANCEL</button>
            </div>
          ) : (
            <>
              <MentionText
                body={m.body}
                mentions={m.mentions}
                myUsername={me?.username}
              />
              {m.edited && <span className="font-mono text-[9px] text-white/40 ml-1" data-testid={`msg-edited-${m.id}`}>(edited)</span>}
            </>
          )}
        </div>
        {reactions.length > 0 && (
          <div className="flex gap-2 mt-1">
            {reactions.map(([emoji, users]) => (
              <button key={emoji} onClick={() => onReact(emoji)} className="border border-white/20 px-2 py-0.5 text-xs hover:border-acid font-mono" data-testid={`reaction-${m.id}-${emoji}`}>
                {emoji} <span className="text-white/60">{users.length}</span>
              </button>
            ))}
          </div>
        )}
        {replyCount > 0 && (
          <button
            type="button"
            onClick={() => onOpenThread && onOpenThread()}
            data-testid={`reply-count-${m.id}`}
            className="mt-1 font-mono text-[10px] tracking-widest text-acid hover:text-white border border-acid/40 hover:border-acid px-2 py-0.5 flex items-center gap-1 w-fit"
            title="Open thread"
          >
            <Reply className="w-3 h-3" /> {replyCount} {replyCount === 1 ? "REPLY" : "REPLIES"}
          </button>
        )}
      </div>
      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-2 mt-1">
        {me && !me.is_guest && (
          <BookmarkBtn
            messageId={m.id}
            sourceType="room"
            sourceId={roomSlug}
            bookmarked={!!bookmarked}
            onChange={onBookmarkChange}
          />
        )}
        <button onClick={onReply} className="text-white/40 hover:text-acid" data-testid={`reply-btn-${m.id}`} title="Reply">
          <Reply className="w-4 h-4" />
        </button>
        <button onClick={onOpenReact} className="text-white/40 hover:text-acid" data-testid={`react-btn-${m.id}`} title="React">
          <Smile className="w-4 h-4" />
        </button>
        {canEdit && !editing && (
          <button onClick={() => setEditing(true)} className="text-white/40 hover:text-acid" data-testid={`edit-btn-${m.id}`} title="Edit">
            <Pencil className="w-4 h-4" />
          </button>
        )}
        {canDelete && (
          <button onClick={onDelete} className="text-white/40 hover:text-blaze" data-testid={`delete-btn-${m.id}`} title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        {!mine && (
          <button onClick={onReport} className="text-white/40 hover:text-blaze" data-testid={`report-btn-${m.id}`} title="Report">
            <Flag className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
